import { Spinner } from '@consigli/facade';
import { useGetBlobsQuery } from '@consigli/hooks';
import { StatusVariant } from '@consigli/types';
import { useMemo } from 'react';

import { Alert, Field, Fieldset, NumberInput, Select, TextArea } from '@/components';

import { ConflictFieldset } from './conflict-fieldset';
import { FindingFormData, FormErrors } from './types';

interface DocumentFindingFormProps {
  projectId: number;
  packageId: string;
  formData: FindingFormData;
  errors: FormErrors;
  updateFormData: (field: keyof FindingFormData, value: unknown) => void;
}

export const DocumentFindingForm = ({
  projectId,
  packageId,
  formData,
  errors,
  updateFormData,
}: DocumentFindingFormProps) => {
  const { data, isLoading } = useGetBlobsQuery({
    projectId,
    packageId,
    page: 'all',
  });

  const blobOptions = useMemo(() => {
    return (
      data?.results.map((blob) => ({
        label: blob.name,
        value: blob.id,
      })) ?? []
    );
  }, [data]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Fieldset legend="Document Finding">
        <Field
          label="Select Document"
          htmlFor="document-select"
          required
          error={errors.selectedDocument}
        >
          <Select
            options={blobOptions}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => String(option.value)}
            value={blobOptions.find((option) => String(option.value) === formData.selectedDocument)}
            onValueChange={(option) => {
              updateFormData('selectedDocument', option?.value || null);
            }}
            className="w-full"
          />
        </Field>
        <Field label="Page Number" htmlFor="page-number" error={errors.pageNumber}>
          <NumberInput
            placeholder="Enter the page number"
            className="w-full"
            value={formData.pageNumber || ''}
            onChange={(e) =>
              updateFormData('pageNumber', e.target.value ? Number(e.target.value) : null)
            }
          />
        </Field>
        <Field
          label="Text in document"
          htmlFor="document-text"
          className="flex flex-col gap-4"
          required
          error={errors.textInDocument}
        >
          <TextArea
            rows={5}
            placeholder="Enter the text that will be highlighted in the document"
            className="w-full"
            value={formData.textInDocument || ''}
            onChange={(e) => updateFormData('textInDocument', e.target.value)}
          />
          <Alert
            variant={StatusVariant.WARNING}
            title="Note"
            description="PDF text highlighting currently only works across a single line. If you are adding a HI-finding, ensure that the text is on a single line. We are working on a fix."
          />
        </Field>
      </Fieldset>
      <ConflictFieldset
        legend="Conflict"
        formData={formData}
        errors={errors}
        updateFormData={updateFormData}
      />
    </>
  );
};
